(function () {
  'use strict';

  function formatMoney(amount, moneyFormat) {
    var value = amount.toFixed(2);
    return (moneyFormat || '${{amount}}').replace('{{amount}}', value);
  }

  function getApplicableTier(qty, tiers) {
    return tiers.reduce(function (best, tier) {
      return qty >= tier.min_qty ? tier : best;
    }, tiers[0]);
  }

  var PricingTiers = function (wrapper) {
    this.wrapper = wrapper;
    this.sectionId = wrapper.dataset.sectionId;
    this.moneyFormat = wrapper.dataset.moneyFormat || '${{amount}}';
    this.tiers = null;
    this.variantsByPrice = null; // price string -> [variantId, ...]
    this.currentQty = 1;
    this.paymentButtonBlockers = [];
  };

  PricingTiers.prototype.readJSON = function (elementId) {
    var el = document.getElementById(elementId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  };

  PricingTiers.prototype.init = function () {
    this.tiers = this.readJSON(this.wrapper.dataset.dataId);
    if (!Array.isArray(this.tiers) || !this.tiers.length) {
      this.wrapper.hidden = true;
      return;
    }

    var variants = this.readJSON(this.wrapper.dataset.variantsId) || [];
    this.variantsByPrice = {};
    variants.forEach(function (variant) {
      var key = variant.price;
      if (!this.variantsByPrice[key]) this.variantsByPrice[key] = [];
      this.variantsByPrice[key].push(variant.id);
    }, this);

    this.render();
    this.bindTierClicks();
    this.bindTierHover();
    this.bindQuantityInput();
  };

  PricingTiers.prototype.render = function () {
    var table = this.wrapper.querySelector('.pricing-tiers-table');
    if (!table) return;

    var thead = document.createElement('thead');
    var tbody = document.createElement('tbody');
    var headerRow = document.createElement('tr');
    var bodyRow = document.createElement('tr');

    this.tiers.forEach(function (tier) {
      var th = document.createElement('th');
      th.dataset.minQty = tier.min_qty;
      th.textContent = tier.min_qty + '+';
      headerRow.appendChild(th);

      var td = document.createElement('td');
      td.dataset.minQty = tier.min_qty;
      td.textContent = formatMoney(tier.price, this.moneyFormat);
      bodyRow.appendChild(td);
    }, this);

    thead.appendChild(headerRow);
    tbody.appendChild(bodyRow);
    table.appendChild(thead);
    table.appendChild(tbody);
  };

  PricingTiers.prototype.highlightDisplayOnly = function (qty) {
    var tier = getApplicableTier(qty, this.tiers);

    this.wrapper.querySelectorAll('[data-min-qty]').forEach(function (cell) {
      cell.classList.toggle(
        'pricing-tiers-table__col--active',
        parseInt(cell.dataset.minQty, 10) === tier.min_qty
      );
    });

    var unitEl = this.wrapper.querySelector('.pricing-tiers__unit-label');
    var totalEl = this.wrapper.querySelector('.pricing-tiers__total-label');
    if (unitEl) unitEl.textContent = formatMoney(tier.price, this.moneyFormat) + ' / item';
    if (totalEl) totalEl.textContent = 'Total: ' + formatMoney(tier.price * qty, this.moneyFormat);

    return tier;
  };

  PricingTiers.prototype.bindTierClicks = function () {
    this.wrapper.querySelectorAll('[data-min-qty]').forEach(function (cell) {
      cell.addEventListener('click', function () {
        // this.quantityInput is set by bindQuantityInput once it has resolved
        // the real input -- reuse it instead of re-querying the DOM. It may
        // still be unset (bindQuantityInput hasn't run yet, or found none).
        var qtyInput = this.quantityInput;
        if (!qtyInput) return;
        qtyInput.value = cell.dataset.minQty;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }.bind(this));
    }, this);
  };

  PricingTiers.prototype.findQuantityInput = function () {
    // The pricing-tiers block and the real add-to-cart form are siblings
    // within the same rendered section (neither is an ancestor of the
    // other), so scope to the shared section wrapper instead of walking up
    // to a <form> -- Shopify's standard per-section wrapper, bounded to
    // just this product, not document-wide.
    var sectionScope = this.wrapper.closest('.shopify-section');
    if (!sectionScope) return null;
    var component = sectionScope.querySelector('quantity-selector-component, cart-quantity-selector-component');
    if (!component) return null;
    return component.querySelector('input[type="number"]');
  };

  PricingTiers.prototype.bindQuantityInput = function () {
    var input = this.findQuantityInput();
    if (!input) {
      // Fail closed: without a quantity input to validate against, nothing
      // confirms the form's actual variant matches whatever tier/price the
      // table is displaying. Block add-to-cart rather than silently leaving
      // it enabled.
      this.showError(
        this.wrapper.querySelector('.pricing-tiers__error'),
        "This product's pricing cannot be verified right now."
      );
      return;
    }

    input.dataset.pricingTiersQuantity = this.sectionId;
    this.quantityInput = input;

    var moq = this.tiers[0].min_qty;
    if (!input.value || parseInt(input.value, 10) < moq) {
      input.value = moq;
    }
    input.min = moq;
    input.dataset.min = moq;

    // Live highlight while typing -- no MOQ enforcement mid-keystroke, so
    // typing "1000" over "500" doesn't snap back on the intermediate "1", "10"...
    input.addEventListener('input', function () {
      var qty = parseInt(input.value, 10) || 0;
      this.highlightDisplayOnly(qty);
    }.bind(this));

    // MOQ enforced only on commit (blur/Enter/programmatic change from a
    // tier click) -- same reasoning as the proven Dawn reference.
    var commitQuantity = function () {
      var qty = parseInt(input.value, 10) || 0;
      if (qty < moq) {
        input.value = moq;
        qty = moq;
      }
      this.highlightDisplayOnly(qty);
      this.resolveAndSelectVariant(qty);
    }.bind(this);
    input.addEventListener('change', commitQuantity);

    // Horizon's quantity-selector-component/cart-quantity-selector-component
    // set the input's value via a plain JS property assignment when its +/-
    // stepper buttons are clicked (component-quantity-selector.js's
    // updateQuantity()) -- a script assignment never fires a native `input`
    // or `change` event, so the two listeners above never see a stepper
    // click. The component instead dispatches its own bubbling custom event,
    // 'quantity-selector:update' (assets/events.js's ThemeEvents.quantity
    // SelectorUpdate -- inlined as a literal here since this file is a
    // classic, non-module script per Task 3's decision and can't import it).
    // Without this listener, using the stepper buttons alone leaves the
    // previously-resolved tier/variant stale.
    var component = input.closest('quantity-selector-component, cart-quantity-selector-component');
    if (component) {
      component.addEventListener('quantity-selector:update', commitQuantity);
    }

    this.currentQty = moq;
    this.highlightDisplayOnly(moq);
    // Defer to after Horizon's own variant-picker connectedCallback has run
    // (it executes synchronously on connect) -- otherwise our initial
    // selection can be overwritten by Horizon's own default-variant init.
    window.setTimeout(function () {
      this.resolveAndSelectVariant(moq);
    }.bind(this), 0);
  };

  PricingTiers.prototype.resolveAndSelectVariant = function (qty) {
    var errorEl = this.wrapper.querySelector('.pricing-tiers__error');
    var tier = getApplicableTier(qty, this.tiers);
    var priceKey = tier.price.toFixed(2);
    var candidateVariantIds = this.variantsByPrice[priceKey] || [];

    if (!candidateVariantIds.length) {
      this.showError(errorEl, 'This quantity is not available for this product right now.');
      return false;
    }

    // Find the hidden "Pricing Tier" radio whose OWN data-variant-id already
    // resolves to one of the candidate variants -- Shopify computes this
    // server-side (product_option_value.variant.id) accounting for whatever
    // other option (e.g. color) is currently selected, so this never needs
    // its own color-tracking logic.
    // Scoped the same way as every other DOM lookup in this file -- document-
    // wide would risk resolving (and .click()-ing) a DIFFERENT product's
    // radio on a page with more than one such fieldset (quick-add dialog,
    // featured-product section).
    var sectionScope = this.wrapper.closest('.shopify-section');
    var fieldset = sectionScope ? sectionScope.querySelector('fieldset[data-option-name="Pricing Tier"]') : null;
    if (!fieldset) {
      this.showError(errorEl, 'This quantity is not available for this product right now.');
      return false;
    }

    var radios = Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
    var targetRadio = radios.filter(function (radio) {
      return candidateVariantIds.indexOf(parseInt(radio.dataset.variantId, 10)) !== -1;
    })[0];

    if (!targetRadio) {
      this.showError(errorEl, 'This quantity is not available for this product right now.');
      return false;
    }

    this.hideError(errorEl);
    if (!targetRadio.checked) targetRadio.click();
    return true;
  };

  PricingTiers.prototype.showError = function (errorEl, message) {
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
    // Same scoping fix as findQuantityInput: the pricing-tiers wrapper and
    // the real add-to-cart form are siblings within the same
    // .shopify-section, never nested -- closest('form') never finds it.
    var sectionScope = this.wrapper.closest('.shopify-section');
    if (sectionScope) {
      sectionScope.querySelectorAll('[type="submit"], [name="add"]').forEach(function (button) {
        // A button the theme already disabled means sold out (or another
        // feature's own hold). Never touch those -- in either direction --
        // so hideError's unconditional re-enable of stamped buttons stays
        // correct: it only ever stamped buttons pricing-tiers is allowed to
        // touch. Mirrors trucustom.js's collectSubmitButtons.
        if (button.disabled) return;
        button.disabled = true;
        button.dataset.pricingTiersBlocked = 'true';
      });
      this.blockAcceleratedCheckout(sectionScope);
    }
  };

  PricingTiers.prototype.hideError = function (errorEl) {
    if (errorEl) errorEl.hidden = true;
    var sectionScope = this.wrapper.closest('.shopify-section');
    if (sectionScope) {
      sectionScope.querySelectorAll('[data-pricing-tiers-blocked="true"]').forEach(function (button) {
        button.disabled = false;
        delete button.dataset.pricingTiersBlocked;
      });
    }
    this.unblockAcceleratedCheckout();
  };

  // ── Dynamic checkout (Buy it now / Shop Pay / PayPal / etc) ───────────────
  //
  // `{{ form | payment_button }}` renders into its own
  // [data-shopify="payment-button"] container that completely bypasses the
  // add-to-cart form -- disabling [type="submit"]/[name="add"] above does
  // nothing for it. Several of the payment methods it can draw (Shop Pay,
  // PayPal, Google/Apple Pay) render into a cross-origin iframe, so there is
  // no click event reachable via a normal listener either. A same-origin
  // overlay physically on top of the container is the one thing that still
  // works, because the browser delivers the click to whatever's on top.
  //
  // Mirrors trucustom.js's findPaymentButtonContainers/
  // blockAcceleratedCheckout/unblockAcceleratedCheckout, but scoped to this
  // block's own .shopify-section (unlike TruCustom's document-wide query,
  // which is safe there because TruCustom guards a whole page's single
  // product) -- a page with more than one product (quick-add dialog,
  // featured-product section) must not have this block another product's
  // checkout button.
  PricingTiers.prototype.blockAcceleratedCheckout = function (sectionScope) {
    if (this.paymentButtonBlockers.length) return; // idempotent

    var containers = Array.prototype.slice.call(
      sectionScope.querySelectorAll('[data-shopify="payment-button"]')
    );
    containers.forEach(function (container) {
      // The overlay is `position: absolute`, which needs a positioned
      // ancestor to size against. Only set one when the theme hasn't already
      // -- never overwrites a value the theme itself set on purpose.
      var previousPosition = container.style.position;
      if (!previousPosition && window.getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }

      var overlay = document.createElement('button');
      overlay.type = 'button'; // must never submit any surrounding form
      overlay.className = 'pricing-tiers__checkout-overlay';
      overlay.setAttribute('aria-label', 'This quantity is not available for this product right now.');
      // The inline .pricing-tiers__error message is already visible
      // whenever this overlay exists (both are created/removed together by
      // showError/hideError), so the click handler only needs to swallow
      // the click -- no second UI system needed here.
      overlay.addEventListener('click', function (event) {
        event.preventDefault();
      });

      container.appendChild(overlay);
      this.paymentButtonBlockers.push({
        container: container,
        overlay: overlay,
        previousPosition: previousPosition
      });
    }, this);
  };

  PricingTiers.prototype.unblockAcceleratedCheckout = function () {
    this.paymentButtonBlockers.forEach(function (entry) {
      if (entry.overlay.parentNode) entry.overlay.parentNode.removeChild(entry.overlay);
      // Restores whatever the theme's own stylesheet says rather than
      // hardcoding a guess at what it was before.
      entry.container.style.position = entry.previousPosition;
    });
    this.paymentButtonBlockers = [];
  };

  PricingTiers.prototype.bindTierHover = function () {
    this.wrapper.querySelectorAll('[data-min-qty]').forEach(function (cell) {
      cell.addEventListener('mouseenter', function () {
        this.wrapper
          .querySelectorAll('[data-min-qty="' + cell.dataset.minQty + '"]')
          .forEach(function (c) { c.classList.add('pricing-tiers-table__col--hover'); });
      }.bind(this));
      cell.addEventListener('mouseleave', function () {
        this.wrapper
          .querySelectorAll('[data-min-qty="' + cell.dataset.minQty + '"]')
          .forEach(function (c) { c.classList.remove('pricing-tiers-table__col--hover'); });
      }.bind(this));
    }, this);
  };

  window.PricingTiersInstances = window.PricingTiersInstances || {};

  function boot() {
    document.querySelectorAll('[data-pricing-tiers]').forEach(function (wrapper) {
      var instance = new PricingTiers(wrapper);
      window.PricingTiersInstances[wrapper.dataset.sectionId] = instance;
      instance.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
